import React from 'react';
import { motion } from 'framer-motion';
import {
  CalendarIcon,
  MapPinIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

// First Sunday game was in January 2022; the count updates itself every week.
const FIRST_SUNDAY = new Date(2022, 0, 2);
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const countSundaysSinceStart = () =>
  Math.floor((Date.now() - FIRST_SUNDAY.getTime()) / WEEK_MS) + 1;

const BerlinBeachVibes: React.FC = () => {
  const sundays = countSundaysSinceStart();

  const stats = [
    { value: "2,000+", label: "Community Members", color: "text-orange-600", bg: "bg-orange-50" },
    { value: `${sundays}`, label: "Sunday Games Organized", color: "text-rose-600", bg: "bg-rose-50" },
    { value: "Since 2022", label: "Every Sunday", color: "text-teal-600", bg: "bg-teal-50" },
    { value: "Solo", label: "Founder", color: "text-sky-600", bg: "bg-sky-50" }
  ];

  const responsibilities = [
    {
      title: "Founder",
      text: "Started Berlin Beach Vibes on my own in 2022 and grew it into a community of more than 2,000 people who play beach volleyball together in Berlin.",
      dot: "bg-orange-500"
    },
    {
      title: "Weekly Organizer",
      text: `Organizing a game every Sunday since 2022, ${sundays} Sundays so far: courts, schedules, teams and making sure newcomers get on the sand from their first game.`,
      dot: "bg-rose-500"
    },
    {
      title: "Community Builder",
      text: "Bringing together people from all over the world, whether they're new to Berlin or have lived here for years, and every level from first-timers to experienced players.",
      dot: "bg-teal-500"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-orange-500 via-rose-500 to-teal-500 p-8">
          <div className="flex items-center space-x-4 text-white">
            <div className="p-2 bg-white rounded-xl flex-shrink-0">
              <img
                src="/companies/berlin-beach-vibes.jpg"
                alt="Berlin Beach Vibes Logo"
                className="h-12 w-28 object-contain"
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-1 text-white">Founder & Organizer</h3>
              <span className="text-white/90 font-medium">Berlin Beach Vibes</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Header Info */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 mb-8 pb-6 border-b border-gray-200">
            <div className="flex items-center">
              <MapPinIcon className="h-4 w-4 mr-2 text-primary-600" />
              <span className="font-medium">Berlin, Germany</span>
            </div>
            <div className="flex items-center">
              <CalendarIcon className="h-4 w-4 mr-2 text-primary-600" />
              <span className="font-medium">2022 – Present · Every Sunday</span>
            </div>
            <div className="flex items-center">
              <UserGroupIcon className="h-4 w-4 mr-2 text-primary-600" />
              <span className="font-medium">Solo Founder</span>
            </div>
          </div>

          {/* Highlights */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {stats.map((stat) => (
              <div key={stat.label} className={`text-center p-4 ${stat.bg} rounded-xl`}>
                <div className={`text-xl font-bold ${stat.color} mb-2`}>{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Role */}
          <div className="mb-8">
            <h4 className="text-xl font-bold text-gray-900 mb-6">My Role</h4>
            <div className="space-y-4">
              {responsibilities.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: (index + 1) * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-4 p-4 bg-gradient-to-r from-gray-50 to-orange-50 rounded-lg"
                >
                  <div className={`w-2 h-2 rounded-full ${item.dot} mt-3 flex-shrink-0`}></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>{item.title}:</strong> {item.text}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Photo */}
          <div className="mb-8 overflow-hidden rounded-xl shadow-md">
            <img
              src="/personal_images/berlin-beach-vibes/group.jpg"
              alt="Berlin Beach Vibes community after a Sunday game"
              loading="lazy"
              className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>

          {/* Focus Areas */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">What It Takes</h4>
            <div className="flex flex-wrap gap-3">
              {["Community Building", "Event Organization", "Consistency", "Inclusion", "International Network", "Beach Volleyball"].map((item, index) => (
                <motion.span
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="px-4 py-2 bg-gradient-to-r from-orange-100 to-rose-100 text-orange-800 text-sm font-medium rounded-full border border-orange-200 hover:shadow-md transition-shadow"
                >
                  {item}
                </motion.span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BerlinBeachVibes;
